import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import EmployeePeek from "@/components/EmployeePeek";
import { FieldError, invalidClass } from "@/components/FieldError";
import { useFieldErrors } from "@/lib/formErrors";
import { useAuth, canWrite } from "@/context/AuthContext";
import { plural } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import Paginator, { usePaged } from "@/components/Paginator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Plus, KeyRound, Upload, Pencil, Trash2, Undo2 } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import CopyableSecret from "@/components/CopyableSecret";
import { useSearch } from "@/lib/search";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FieldLabel } from "@/components/FieldLabel";
import ComboBox from "@/components/ComboBox";
import SortableTh, { useSorted } from "@/components/SortableTh";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// The boxes both dialogs draw, by the server's names.
const EMP_FIELDS = ["employee_code", "full_name", "designation", "email", "mobile", "date_of_joining", "unit_id", "department_id"];
const REQUIRED = { employee_code: "Employee code", full_name: "Full name", unit_id: "Unit", department_id: "Department" };
const missingIn = (form) => Object.fromEntries(
  Object.entries(REQUIRED).filter(([k]) => !String(form[k] ?? "").trim()).map(([k, label]) => [k, `${label} is required`]));

// Module scope: a stable reference, or the memo is rebuilt every render.
const EMPLOYEE_FIELDS = ["full_name", "employee_code", "designation", "email", "mobile"];

// How each sortable column is read off an employee row.
const EMP_SORT = {
  code: "employee_code", name: "full_name", designation: "designation",
  department: (e, ctx) => ctx.deptName(e.department_id),
  unit: (e, ctx) => ctx.unitName(e.unit_id),
  email: "email",
  login: (e) => (e.has_login ? 1 : 0),
};

const BLANK = { employee_code: "", full_name: "", designation: "", email: "", mobile: "", unit_id: "", department_id: "", date_of_joining: "" };

/**
 * The departments a person posted to this unit can actually belong to.
 *
 * `departments.unit_id` is NOT NULL — a department belongs to exactly one plant — so
 * offering all of them let somebody in Vadodara be filed under Ankleshwar's Quality.
 * The Excel import had always refused that row; only the form accepted it.
 *
 * `keep` is the department already on the record: an employee saved before this was
 * enforced still has to be visible in their own edit dialog, or opening it would appear
 * to have silently changed their department.
 */
function departmentsInUnit(depts, unitId, keep) {
  if (!unitId) return [];
  return depts.filter((d) => d.unit_id === unitId || (keep && d.id === keep));
}

export default function Employees() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [emps, setEmps] = useState([]);
  const [units, setUnits] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [unit, setUnit] = useState("all");
  const [dept, setDept] = useState("all");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const [editing, setEditing] = useState(null);   // { row, form }
  const [deleting, setDeleting] = useState(null);
  const [retired, setRetired] = useState(null);   // null until asked for
  const [busy, setBusy] = useState(false);
  const [credOpen, setCredOpen] = useState(false);
  const [cred, setCred] = useState(null);
  // Designations HR has already used. A suggestion, not a rule — a new role still has to
  // be typeable — but it stops "Sr. Engineer", "Senior Engineer" and "Sr Engineer" from
  // becoming three different things in the same register.
  const [hints, setHints] = useState({ designations: [] });
  const [sort, setSort] = useState("");
  const addErr = useFieldErrors();
  const editErr = useFieldErrors();
  // Which person's panel is open, in the URL so a refresh or a pasted link reopens it.
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("open") || "";
  const setOpenId = (id) => setSp((prev) => {
    const next = new URLSearchParams(prev);
    if (id) next.set("open", id); else next.delete("open");
    return next;
  }, { replace: true });
  const [importOpen, setImportOpen] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [importing, setImporting] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [e, u, d, s] = await Promise.all([
      api.get("/employees"), api.get("/units"), api.get("/departments"), api.get("/suggestions")]);
    setEmps(e.data); setUnits(u.data); setDepts(d.data); setHints(s.data);
  };
  useEffect(() => { load(); }, []);

  // Changing the unit clears a department belonging to the old one, rather than leaving
  // a selection that is no longer on the list and that the server will now refuse.
  const setAddUnit = (unit_id) => setF((s) => ({
    ...s, unit_id,
    department_id: departmentsInUnit(depts, unit_id).some((d) => d.id === s.department_id)
      ? s.department_id : "",
  }));

  const setEditUnit = (unit_id) => setEditing((e) => ({
    ...e,
    form: {
      ...e.form, unit_id,
      department_id: departmentsInUnit(depts, unit_id).some((d) => d.id === e.form.department_id)
        ? e.form.department_id : "",
    },
  }));

  const submit = async () => {
    const gaps = missingIn(f);
    if (Object.keys(gaps).length) { addErr.report({ fields: gaps }); return; }
    try {
      await api.post("/employees", f);
      toast.success("Employee added");
      setOpen(false);
      setF(BLANK);
      addErr.clear();
      load();
    } catch (e) { addErr.report(e, { known: EMP_FIELDS }); }
  };

  // Typing into a box that was refused clears its message; the next save re-checks.
  const setAdd = (patch) => { Object.keys(patch).forEach(addErr.clear); setF((s) => ({ ...s, ...patch })); };
  const setEdit = (patch) => { Object.keys(patch).forEach(editErr.clear); setEditing((e) => ({ ...e, form: { ...e.form, ...patch } })); };

  const createLogin = async (emp) => {
    try {
      const r = await api.post(`/employees/${emp.id}/create-login`);
      setCred({ ...r.data, employee: emp });
      setCredOpen(true);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Answered by /employees itself. It used to mean "fetch every user and look", which is
  // an Admin-only route: for HR Manager the call 403'd, the failure was swallowed, and
  // every row claimed nobody had a login.
  const hasLogin = (emp) => !!emp.has_login;

  // A code or a name is printed on a certificate and answered by the public verify page,
  // so a typo in one had to be correctable rather than retyped as a new person.
  const startEdit = (row) => setEditing({ row, form: { ...BLANK, ...Object.fromEntries(
    Object.keys(BLANK).map((k) => [k, row[k] ?? ""])) } });

  const saveEdit = async () => {
    const gaps = missingIn(editing.form);
    if (Object.keys(gaps).length) { editErr.report({ fields: gaps }); return; }
    setBusy(true);
    try {
      // The whole row goes back, not just the visible fields — the same reason the
      // masters edit does: PUT fills an unmentioned column from the model default.
      await api.put(`/employees/${editing.row.id}`, { ...editing.row, ...editing.form });
      toast.success("Employee updated");
      setEditing(null);
      editErr.clear();
      load();
    } catch (e) { editErr.report(e, { known: EMP_FIELDS }); }
    setBusy(false);
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/employees/${deleting.id}`);
      toast.success(`${deleting.full_name} retired`);
      setDeleting(null);
      load();
      if (retired) loadRetired();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not retire")); }
    setBusy(false);
  };

  const loadRetired = async () => {
    try {
      const r = await api.get("/employees", { params: { deleted: true } });
      setRetired(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not load retired people")); }
  };

  const restore = async (row) => {
    try {
      await api.post(`/employees/${row.id}/restore`);
      toast.success(`${row.full_name} restored`);
      loadRetired();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not restore")); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/employees/bulk-import", fd);
      setImportReport(r.data);
      const failed = r.data.errors?.length || 0;
      const already = r.data.skipped?.length || 0;
      const detail = [already && `${already} already on file`, failed && `${failed} failed`]
        .filter(Boolean).join(" · ");
      const line = `Imported ${plural(r.data.created, "employee")}` + (detail ? ` — ${detail}` : "");
      // A success toast over a screen of red was the contradiction HR read as failure.
      if (failed) toast.warning(line); else toast.success(line);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Import failed"));
    }
    setImporting(false);
  };

  // The selects narrow first; the text query then runs over what is left.
  const scoped = emps.filter((e) => (unit === "all" || e.unit_id === unit)
                                 && (dept === "all" || e.department_id === dept));
  const filtered = useSearch(scoped, q, EMPLOYEE_FIELDS);
  // Sorted by the name a person actually reads in the cell, not by the id stored behind
  // it — sorting Department by uuid would look random.
  const sortCtx = useMemo(() => ({
    deptName: (id) => depts.find((d) => d.id === id)?.name || "",
    unitName: (id) => units.find((u) => u.id === id)?.name || "",
  }), [depts, units]);
  const sortColumns = useMemo(() => Object.fromEntries(
    Object.entries(EMP_SORT).map(([k, read]) => [
      k, typeof read === "function" ? (row) => read(row, sortCtx) : read])
  ), [sortCtx]);
  const sorted = useSorted(filtered, sortColumns, sort);

  const paged = usePaged(sorted);


  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Master data</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Employees</h2>
          <p className="text-sm text-muted-foreground mt-1">{emps.length} people across all units.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {writable && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-emp-btn"><Upload className="w-4 h-4 mr-1.5" /> Import Excel</Button>}
          <Button variant="outline" onClick={() => window.open(`${API}/exports/employees.xlsx`)} data-testid="export-emp-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
          {writable && <Button onClick={() => setOpen(true)} data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-1.5" /> Add employee</Button>}
        </div>
      </div>

      <Card className="p-4 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <SearchInput className="md:col-span-2" value={q} onChange={setQ} testid="emp-search"
                       placeholder="Search name, code, designation or email..." />
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger aria-label="Unit"><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All units</SelectItem>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger aria-label="Dept"><SelectValue placeholder="Dept" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All depts</SelectItem>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-zebra">
          <thead className="bg-muted/70 border-b border-border"><tr className="text-left">
            <SortableTh sortKey="code" sort={sort} onSort={setSort}>Code</SortableTh>
            <SortableTh sortKey="name" sort={sort} onSort={setSort}>Name</SortableTh>
            <SortableTh sortKey="designation" sort={sort} onSort={setSort}>Designation</SortableTh>
            <SortableTh sortKey="department" sort={sort} onSort={setSort}>Department</SortableTh>
            <SortableTh sortKey="unit" sort={sort} onSort={setSort}>Unit</SortableTh>
            <SortableTh sortKey="email" sort={sort} onSort={setSort}>Email</SortableTh>
            <SortableTh sortKey="login" sort={sort} onSort={setSort}>Login</SortableTh>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {paged.slice.map((e) => {
              const d = depts.find((x) => x.id === e.department_id);
              const u = units.find((x) => x.id === e.unit_id);
              const has = hasLogin(e);
              return (
                <tr key={e.id} onClick={() => setOpenId(e.id)} data-testid={`employee-row-${e.employee_code}`}
                    className={`border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer ${openId === e.id ? "bg-primary/5" : ""}`}>
                  <td className="px-4 py-2 font-mono">{e.employee_code}</td>
                  <td className="px-4 py-2 font-medium">{e.full_name}</td>
                  <td className="px-4 py-2">{e.designation}</td>
                  <td className="px-4 py-2">{d?.name}</td>
                  <td className="px-4 py-2">{u?.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{e.email}</td>
                  {/* The buttons act on the row without opening its panel. */}
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    {has ? <Badge variant="outline" className="text-emerald-700 dark:text-emerald-400 border-emerald-500/40">Active</Badge> : (
                      <Button variant="ghost" size="sm" onClick={() => createLogin(e)} disabled={!e.email || !writable} data-testid={`create-login-${e.employee_code}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Create login
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => nav(`/employees/${e.id}`)} data-testid={`view-history-${e.employee_code}`}>History</Button>
                      {writable && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => startEdit(e)}
                                  aria-label={`Edit ${e.full_name}`} data-testid={`edit-emp-${e.employee_code}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleting(e)}
                                  aria-label={`Retire ${e.full_name}`} data-testid={`delete-emp-${e.employee_code}`}>
                            <Trash2 className="w-4 h-4 text-destructive-text" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
        <Paginator paged={paged} label="people" />
      </Card>

      {writable && (
        <Card className="p-4 border border-border">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-semibold text-sm">Retired people</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Retiring hides somebody from every list; their training history and certificates stay.
              </p>
            </div>
            <Button variant="outline" size="sm" data-testid="toggle-retired"
                    onClick={() => (retired ? setRetired(null) : loadRetired())}>
              {retired ? "Hide" : "Show retired"}
            </Button>
          </div>
          {retired && (
            retired.length === 0
              ? <p className="text-sm text-muted-foreground mt-4">Nobody has been retired.</p>
              : <div className="mt-4 divide-y divide-border">
                  {retired.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2 gap-3">
                      <div className="text-sm">
                        <span className="font-mono text-xs text-muted-foreground mr-2">{r.employee_code}</span>
                        {r.full_name}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => restore(r)} data-testid={`restore-emp-${r.employee_code}`}>
                        <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Restore
                      </Button>
                    </div>
                  ))}
                </div>
          )}
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); editErr.clear(); } }}>
        <DialogContent className="max-w-2xl" data-testid="emp-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit employee</DialogTitle>
            <DialogDescription>
              {editing ? `${editing.row.employee_code} — this name and code appear on issued certificates.` : ""}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div><FieldLabel htmlFor="edit-code" required>Code</FieldLabel><Input id="edit-code" value={editing.form.employee_code} onChange={(e) => setEdit({ employee_code: e.target.value })} data-testid="edit-emp-code" className={invalidClass(editErr.errors, "employee_code")} {...editErr.fieldProps("employee_code")} /><FieldError field="employee_code" errors={editErr.errors} /></div>
              <div><FieldLabel htmlFor="edit-name" required>Full name</FieldLabel><Input id="edit-name" value={editing.form.full_name} onChange={(e) => setEdit({ full_name: e.target.value })} data-testid="edit-emp-name" className={invalidClass(editErr.errors, "full_name")} {...editErr.fieldProps("full_name")} /><FieldError field="full_name" errors={editErr.errors} /></div>
              <div><FieldLabel htmlFor="edit-desig">Designation</FieldLabel><ComboBox id="edit-desig" testid="edit-desig" value={editing.form.designation} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, designation: v } })} options={hints.designations || []} placeholder="e.g. Sr. Engineer" /></div>
              <div><FieldLabel htmlFor="edit-email">Email</FieldLabel><Input id="edit-email" value={editing.form.email} onChange={(e) => setEdit({ email: e.target.value })} className={invalidClass(editErr.errors, "email")} {...editErr.fieldProps("email")} /><FieldError field="email" errors={editErr.errors} /></div>
              <div><FieldLabel htmlFor="edit-mobile">Mobile</FieldLabel><Input id="edit-mobile" value={editing.form.mobile} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, mobile: e.target.value } })} /></div>
              <div><FieldLabel htmlFor="edit-doj">DOJ</FieldLabel><Input id="edit-doj" type="date" value={editing.form.date_of_joining || ""} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, date_of_joining: e.target.value } })} /></div>
              <div {...editErr.fieldProps("unit_id")}>
                <FieldLabel required>Unit</FieldLabel>
                <Select value={editing.form.unit_id} onValueChange={(v) => { editErr.clear("unit_id"); setEditUnit(v); }}><SelectTrigger data-testid="edit-emp-unit" aria-label="Unit"><SelectValue placeholder="Unit" /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
                <FieldError field="unit_id" errors={editErr.errors} />
              </div>
              <div {...editErr.fieldProps("department_id")}>
                <FieldLabel required>Department</FieldLabel>
                {(() => {
                  const options = departmentsInUnit(depts, editing.form.unit_id, editing.row.department_id);
                  return options.length === 0 ? (
                    <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md px-3 py-2.5" data-testid="edit-emp-dept-empty">
                      {editing.form.unit_id ? "This unit has no departments yet — add one under Masters."
                                            : "Pick a unit first."}
                    </p>
                  ) : (
                    <Select value={editing.form.department_id} onValueChange={(v) => setEdit({ department_id: v })}><SelectTrigger data-testid="edit-emp-dept" aria-label="Dept"><SelectValue placeholder="Dept" /></SelectTrigger><SelectContent>{options.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
                  );
                })()}
                <FieldError field="department_id" errors={editErr.errors} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="emp-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent data-testid="emp-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retire "{deleting?.full_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              They leave every list, dropdown and nomination picker, and <b>their login is revoked
              immediately</b> &mdash; including any session they already have open. Attendance
              history and issued certificates are untouched and keep verifying. Refused only while
              they are still registered as a trainer. Restoring brings the person back but not the
              login; use "Create login" for that.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={busy}
                               className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                               data-testid="emp-delete-confirm">Retire</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) addErr.clear(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><FieldLabel htmlFor="emp-code" required>Code</FieldLabel><Input id="emp-code" value={f.employee_code} onChange={(e) => setAdd({ employee_code: e.target.value })} data-testid="emp-code" className={invalidClass(addErr.errors, "employee_code")} {...addErr.fieldProps("employee_code")} /><FieldError field="employee_code" errors={addErr.errors} /></div>
            <div><FieldLabel htmlFor="emp-name" required>Full name</FieldLabel><Input id="emp-name" value={f.full_name} onChange={(e) => setAdd({ full_name: e.target.value })} data-testid="emp-name" className={invalidClass(addErr.errors, "full_name")} {...addErr.fieldProps("full_name")} /><FieldError field="full_name" errors={addErr.errors} /></div>
            <div><FieldLabel htmlFor="emp-desig">Designation</FieldLabel><ComboBox id="emp-desig" testid="emp-desig" value={f.designation} onChange={(v) => setF({ ...f, designation: v })} options={hints.designations || []} placeholder="e.g. Sr. Engineer" /></div>
            <div><FieldLabel htmlFor="emp-email">Email</FieldLabel><Input id="emp-email" value={f.email} onChange={(e) => setAdd({ email: e.target.value })} className={invalidClass(addErr.errors, "email")} {...addErr.fieldProps("email")} /><FieldError field="email" errors={addErr.errors} /></div>
            <div><FieldLabel htmlFor="emp-mobile">Mobile</FieldLabel><Input id="emp-mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></div>
            <div><FieldLabel htmlFor="emp-doj">DOJ</FieldLabel><Input id="emp-doj" type="date" value={f.date_of_joining} onChange={(e) => setF({ ...f, date_of_joining: e.target.value })} /></div>
            <div {...addErr.fieldProps("unit_id")}>
              <FieldLabel required>Unit</FieldLabel>
              <Select value={f.unit_id} onValueChange={(v) => { addErr.clear("unit_id"); setAddUnit(v); }}><SelectTrigger data-testid="emp-unit" aria-label="Unit"><SelectValue placeholder="Unit" /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
              <FieldError field="unit_id" errors={addErr.errors} />
            </div>
            <div {...addErr.fieldProps("department_id")}>
              <FieldLabel required>Department</FieldLabel>
              {(() => {
                const options = departmentsInUnit(depts, f.unit_id);
                return options.length === 0 ? (
                  <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md px-3 py-2.5" data-testid="emp-dept-empty">
                    {f.unit_id ? "This unit has no departments yet — add one under Masters."
                               : "Pick a unit first — a department belongs to one."}
                  </p>
                ) : (
                  <Select value={f.department_id} onValueChange={(v) => setAdd({ department_id: v })}><SelectTrigger data-testid="emp-dept" aria-label="Dept"><SelectValue placeholder="Dept" /></SelectTrigger><SelectContent>{options.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
                );
              })()}
              <FieldError field="department_id" errors={addErr.errors} />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="emp-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent data-testid="cred-dialog">
          <DialogHeader><DialogTitle>Employee login created</DialogTitle><DialogDescription>The password is shown once and cannot be retrieved afterwards.</DialogDescription></DialogHeader>
          {cred && (
            <div className="space-y-3 text-sm">
              <p>Share these credentials with <b>{cred.employee.full_name}</b>. They will be prompted to set a new password on first login.</p>
              <div className="p-3 rounded-md bg-muted border border-border font-mono text-sm space-y-1">
                <CopyableSecret label="Email" value={cred.email} testid="cred-email" />
                {cred.default_password && <CopyableSecret label="Password" value={cred.default_password} testid="cred-password" />}
                {cred.linked && <div className="text-xs text-muted-foreground">Existing user account linked — password unchanged.</div>}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setCredOpen(false)} data-testid="cred-close">Got it</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportReport(null); }}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader><DialogTitle>Bulk import employees</DialogTitle><DialogDescription>Upload an Excel (.xlsx) with columns: employee_code, full_name, designation, unit_name, department_name, email, mobile, date_of_joining.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Button variant="outline" size="sm" onClick={async () => { const r = await api.get("/employees/import-template.xlsx", { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "employees-import-template.xlsx"; a.click(); }} data-testid="download-template"><Download className="w-4 h-4 mr-1.5" /> Download template</Button>
            <Label className="flex flex-col items-center gap-2 cursor-pointer border-2 border-dashed border-border rounded-md p-8 hover:bg-muted/50" data-testid="import-drop">
              <Upload className="w-6 h-6 text-primary" />
              <div className="text-sm">{importing ? "Importing..." : "Click to choose .xlsx file"}</div>
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} data-testid="import-file-input" />
            </Label>
            {importReport && (
              /* Three counts, not two. The fix for a rejected row is to correct it and
                 re-upload the whole file, and everybody already imported then came back
                 as "failed" — 199 red lines under a success toast, which reads as a
                 failed import and invites a third upload. Nothing was wrong. */
              <div className="border border-border rounded-md p-3 text-sm space-y-2">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <div className="text-emerald-700 dark:text-emerald-400"><b>{importReport.created}</b> imported</div>
                  {importReport.skipped?.length > 0 && (
                    <div className="text-muted-foreground"><b>{importReport.skipped.length}</b> already on file</div>
                  )}
                  <div className={importReport.errors?.length ? "text-rose-700 dark:text-rose-400" : "text-muted-foreground"}>
                    <b>{importReport.errors?.length || 0}</b> failed
                  </div>
                  <div className="text-muted-foreground">of {importReport.total_rows}</div>
                </div>
                {importReport.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                    {importReport.errors.map((e, i) => <div key={i} className="text-rose-700 dark:text-rose-400">Row {e.row}: {e.error}</div>)}
                  </div>
                )}
                {importReport.skipped?.length > 0 && importReport.errors?.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nothing failed. Re-uploading a corrected file is expected to skip
                    everybody it imported the first time.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={() => setImportOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <EmployeePeek id={openId} onClose={() => setOpenId("")} lookup={sortCtx}
                    onEdit={(row) => { setOpenId(""); startEdit({ ...row, has_login: row.has_login }); }} />
    </div>
  );
}
